import { useContext, useEffect, useState } from 'react';
import axios from '@helpers/axios';
import { Box, Button, Layer, Select, Text, TextArea, TextInput } from 'grommet';
import { Close } from 'grommet-icons';
import * as Sentry from '@sentry/browser';
import { TeamContext } from '@services/Session';

interface VideoSettingsProps {
  onClose: () => void;
}
export const VideoSettings: React.FC<VideoSettingsProps> = ({ onClose }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [privacy, setPrivacy] = useState('public');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const team = useContext(TeamContext);
  const teamId = team.id;

  const privacyOptions = [
    { label: 'Public', value: 'public' },
    { label: 'Public (not listed)', value: 'unlisted' },
    { label: 'Private', value: 'private' },
  ];

  useEffect(() => {
    if (team) {
      axios.get('/api/youtube_auth', { params: { teamId } }).then((res) => {
        const channelInfo = res.data;
        setLoading(false);
        if (channelInfo) {
          const defaultTeamTitle = team.nameMed;
          const defaultDescription = `See highlights at https://sidelineHD.com/${team.nameHandle} \n\n#sidelineHD #${team.attrib_sportType} #sidelineHDLIVE`;
          setTitle(channelInfo.nextTitle || defaultTeamTitle);
          setDescription(channelInfo.nextDescription || defaultDescription);
          setPrivacy(channelInfo.nextPrivacy || 'public');
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team]);

  const onSave = () => {
    setLoading(true);
    axios
      .put('/api/youtube_auth', {
        data: {
          nextTitle: title,
          nextDescription: description,
          nextPrivacy: privacy,
        },
        teamId,
      })
      .then(() => {
        setLoading(false);
        onClose();
      })
      .catch((e) => {
        Sentry.captureException(e);
        setLoading(false);
        setError(e.response.data.message);
      });
  };

  return (
    <Layer modal onClickOutside={onClose}>
      <Box margin="small">
        <Box margin="small" direction="row" justify="between">
          <Text alignSelf="center" size="large" weight="bold">
            YouTube Settings
          </Text>
          <Close onClick={onClose} />
        </Box>
        <Box>
          {error && <Text color="status-error">{error}</Text>}
          {loading ? (
            <Text>Loading...</Text>
          ) : (
            <Box gap="small">
              <Text>For your next broadcast...</Text>
              <TextInput
                placeholder="Title"
                value={title}
                onChange={(e) => {
                  setError(null);
                  setTitle(e.target.value);
                }}
              />
              <Box height="200px">
                <TextArea
                  placeholder="Description"
                  value={description}
                  onChange={(e) => {
                    setError(null);
                    setDescription(e.target.value);
                  }}
                  fill
                />
              </Box>
              <Box>
                <Text>Visibility of the video:</Text>
                <Select
                  options={privacyOptions}
                  value={privacy}
                  labelKey="label"
                  valueKey={{ key: 'value', reduce: true }}
                  onChange={({ option }) => setPrivacy(option.value)}
                />
              </Box>
              <Box margin={{ top: 'mediium' }}>
                <Button
                  primary
                  color="tertiary-1"
                  label="Save"
                  onClick={onSave}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Layer>
  );
};
